import React, {useCallback, useEffect, useMemo, useState} from 'react'
import './RichTextInput.scss'
import {Editable, withReact, useSlate, Slate} from 'slate-react'
import {
  Editor,
  Transforms,
  createEditor,
  Element as SlateElement,
} from 'slate'
import {withHistory} from 'slate-history'
import {FormLabel, IconButton} from "@carbon/react";
import {
  TextBold,
  TextItalic,
  TextUnderline,
  Code,
  Number_1,
  Number_2,
  Quotes,
  ListNumbered,
  ListBulleted,
  TextAlignLeft,
  TextAlignCenter,
  TextAlignRight,
  TextAlignJustify,
  Link,
  Phone
} from '@carbon/react/icons';
import PropTypes from "prop-types";
import TextAreaSkeleton from "@carbon/react/lib/components/TextArea/TextArea.Skeleton";

const LIST_TYPES = ['numbered-list', 'bulleted-list']
const TEXT_ALIGN_TYPES = ['left', 'center', 'right', 'justify']

const RichTextInput = (props) => {
  const renderElement = useCallback(props => <Element {...props} />, [])
  const renderLeaf = useCallback(props => <Leaf {...props} />, [])
  const editor = useMemo(() => withHistory(withReact(createEditor())), [])
  const [value, setValue] = useState(null)

  const isJson = (str) => {
    try {
      JSON.parse(str);
    } catch (e) {
      return false;
    }
    return true;
  }

  const jsonFromText = (str) => {
    if (isJson(str)) {
      return JSON.parse(str)
    }
    // enforce string typing
    str = str + ""
    return [{
      type: 'paragraph',
      children: str.split(" ").map(it => {
        return {text: it}
      }),
    }]
  }

  useEffect(() => {
    setValue(props.value)
  }, [props.value])

  if (value == null) {
    return (
      <TextAreaSkeleton />
    )
  }

  return (
    <>
      {props.labelText && <FormLabel>{props.labelText}</FormLabel>}
      <Slate editor={editor} initialValue={jsonFromText(value)} onChange={(desc) => {
        props.onChange(JSON.stringify(desc))
      }}>
        <div className="richtext-toolbar">
          <MarkButton format="bold" label="Grassetto" icon={<TextBold/>}/>
          <MarkButton format="italic" label="Corsivo" icon={<TextItalic/>}/>
          <MarkButton format="underline" label="Sottolineato" icon={<TextUnderline/>}/>
          <MarkButton format="code" label="Codice" icon={<Code/>}/>
          <MarkButton format="link" label="Link" icon={<Link/>}/>
          <MarkButton format="phone" label="Telefono" icon={<Phone/>}/>
          <BlockButton format="heading-one" label="Titolo 1" icon={<Number_1/>}/>
          <BlockButton format="heading-two" label="Titolo 2" icon={<Number_2/>}/>
          <BlockButton format="block-quote" label="Citazione" icon={<Quotes/>}/>
          <BlockButton format="numbered-list" label="Lista numerata" icon={<ListNumbered/>}/>
          <BlockButton format="bulleted-list" label="Lista con pallini" icon={<ListBulleted/>}/>
          <BlockButton format="left" label="Allinea a sinistra" icon={<TextAlignLeft/>}/>
          <BlockButton format="center" label="Allinea in centro" icon={<TextAlignCenter/>}/>
          <BlockButton format="right" label="Allinea a destra" icon={<TextAlignRight/>}/>
          <BlockButton format="justify" label="Giustifica" icon={<TextAlignJustify/>}/>
        </div>
        <Editable
          renderElement={renderElement}
          renderLeaf={renderLeaf}
          spellCheck
          className="richtext-content"
        />
      </Slate>
    </>
  )
}

const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(
    editor,
    format,
    TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type'
  )
  const isList = LIST_TYPES.includes(format)

  Transforms.unwrapNodes(editor, {
    match: n =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes(n.type) &&
      !TEXT_ALIGN_TYPES.includes(format),
    split: true,
  })
  let newProperties
  if (TEXT_ALIGN_TYPES.includes(format)) {
    newProperties = {
      align: isActive ? undefined : format,
    }
  } else {
    newProperties = {
      type: isActive ? 'paragraph' : isList ? 'list-item' : format,
    }
  }
  Transforms.setNodes(editor, newProperties)

  if (!isActive && isList) {
    const block = {type: format, children: []}
    Transforms.wrapNodes(editor, block)
  }
}

const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format)
  if (isActive) {
    Editor.removeMark(editor, format)
  } else {
    Editor.addMark(editor, format, true)
  }
}

const isBlockActive = (editor, format, blockType = 'type') => {
  const {selection} = editor
  if (!selection) return false

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: n =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n[blockType] === format,
    })
  )

  return !!match
}

const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor)
  return marks ? marks[format] === true : false
}

const Element = ({attributes, children, element}) => {
  const style = {textAlign: element.align}
  switch (element.type) {
    case 'block-quote':
      return (
        <blockquote style={style} {...attributes}>
          {children}
        </blockquote>
      )
    case 'bulleted-list':
      return (
        <ul style={style} {...attributes}>
          {children}
        </ul>
      )
    case 'heading-one':
      return (
        <h1 style={style} {...attributes}>
          {children}
        </h1>
      )
    case 'heading-two':
      return (
        <h2 style={style} {...attributes}>
          {children}
        </h2>
      )
    case 'list-item':
      return (
        <li style={style} {...attributes}>
          {children}
        </li>
      )
    case 'numbered-list':
      return (
        <ol style={style} {...attributes}>
          {children}
        </ol>
      )
    default:
      return (
        <p style={style} {...attributes}>
          {children}
        </p>
      )
  }
}

const isValidHttpUrl = (string) => {
  let url;
  try {
    url = new URL(string);
  } catch (_) {
    return false;
  }
  return url.protocol === "http:" || url.protocol === "https:";
}

const Leaf = ({attributes, children, leaf}) => {
  if (leaf.bold) {
    children = <strong>{children}</strong>
  }

  if (leaf.code) {
    children = <code>{children}</code>
  }

  if (leaf.italic) {
    children = <em>{children}</em>
  }

  if (leaf.underline) {
    children = <u>{children}</u>
  }

  if (leaf.link) {
    if (children.props.text && isValidHttpUrl(children.props.text.text)) {
      children = <a target="_blank" href={children.props.text.text}>{children}</a>
    }
  }

  if (leaf.phone) {
    if (children.props.text && children.props.text.text) {
      children = <a href={"tel:" + children.props.text.text}>{children}</a>
    }
  }

  return <span {...attributes}>{children}</span>
}

const BlockButton = ({format, label, icon}) => {
  const editor = useSlate()
  return (
    <IconButton
      kind="secondary"
      label={label}
      className={isBlockActive(
        editor,
        format,
        TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type'
      ) ? "richtext-toolbar-btn-active" : ""}
      onMouseDown={event => {
        event.preventDefault()
        toggleBlock(editor, format)
      }}>
      {icon}
    </IconButton>
  )
}

const MarkButton = ({format, label, icon}) => {
  const editor = useSlate()
  return (
    <IconButton
      kind="secondary"
      label={label}
      className={isMarkActive(editor, format) ? "richtext-toolbar-btn-active" : ""}
      onMouseDown={event => {
        event.preventDefault()
        toggleMark(editor, format)
      }}>
      {icon}
    </IconButton>
  )
}

RichTextInput.propTypes = {
  labelText: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
}

export default RichTextInput